/* ═══════════════════════════════════════════════════════════
   Terrenotrie Ceramiche — theme.js
   Consolidated from mockups/assets/polish.js + index.html inline scripts.
   Behaviours: scroll-fade-up reveals, mobile drawer (with focus trap),
   nav scroll-state, hero scroll cue, intro manifesto cascade, wordmark entrance.
   All guarded for prefers-reduced-motion and degrade gracefully without markup.
   ═══════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  var prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ───── Scroll-fade-up reveals ───── */
  (function () {
    var els = document.querySelectorAll('.reveal');
    if (!els.length) return;
    if (prefersReduced || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry, i) {
        if (entry.isIntersecting) {
          setTimeout(function () { entry.target.classList.add('is-visible'); }, i * 80);
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -60px 0px' });
    els.forEach(function (el) { observer.observe(el); });
  })();

  /* ───── Mobile nav drawer (hamburger → full-screen overlay) ───── */
  (function () {
    var toggle = document.querySelector('.nav-toggle');
    var drawer = document.getElementById('mobile-drawer');
    if (!toggle || !drawer) return;
    var closeBtn = drawer.querySelector('.mobile-drawer-close');
    var links = drawer.querySelectorAll('a');

    // Focusable elements inside the drawer (for the focus trap)
    var focusables = drawer.querySelectorAll(
      'a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])'
    );
    var first = focusables[0];
    var last = focusables[focusables.length - 1];

    function open() {
      drawer.classList.add('open');
      document.body.style.overflow = 'hidden';
      toggle.setAttribute('aria-expanded', 'true');
      if (closeBtn) closeBtn.focus();
    }
    function close() {
      drawer.classList.remove('open');
      document.body.style.overflow = '';
      toggle.setAttribute('aria-expanded', 'false');
      toggle.focus();
    }

    toggle.addEventListener('click', open);
    if (closeBtn) closeBtn.addEventListener('click', close);
    links.forEach(function (a) { a.addEventListener('click', close); });

    document.addEventListener('keydown', function (e) {
      if (!drawer.classList.contains('open')) return;
      if (e.key === 'Escape') { close(); return; }
      // Trap focus inside the drawer while open
      if (e.key === 'Tab' && focusables.length) {
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault(); last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault(); first.focus();
        }
      }
    });
  })();

  /* ───── Nav scroll-state (transparent over hero → frosted after scroll) ───── */
  (function () {
    var nav = document.querySelector('.nav');
    if (!nav) return;
    var threshold = 32;
    var toggleNav = function () {
      nav.classList.toggle('scrolled', window.scrollY > threshold);
    };
    toggleNav();
    window.addEventListener('scroll', toggleNav, { passive: true });
  })();

  /* ───── Measured chrome heights → --nav-h / --page-nav-h ─────
     --nav-h: 72px in CSS is the DESKTOP bar height; on mobile the wordmark
     shrinks to 130px and the real bar is ~62px, which left a 10px gap under
     the Galleria's sticky sub-nav (and mis-set the anchor scroll margins).
     Measure the real heights instead of assuming. ResizeObserver re-fires on
     viewport changes and when the wordmark image loads; without JS the CSS
     fallbacks (72px / 52px) keep today's behavior. --nav-h excludes the
     safe-area padding because every CSS consumer adds env() itself. */
  (function () {
    var nav = document.querySelector('.nav');
    if (!nav || !('ResizeObserver' in window)) return;
    var root = document.documentElement;
    var pageNav = document.querySelector('.page-nav');
    var update = function () {
      var pad = parseFloat(getComputedStyle(nav).paddingTop) || 0;
      /* floor: a fractional bar height must err toward overlap (tucked under
         the higher-z nav), never toward a visible sub-pixel gap */
      var h = Math.floor(nav.getBoundingClientRect().height - pad);
      if (h > 0) root.style.setProperty('--nav-h', h + 'px');
      if (pageNav && pageNav.offsetHeight > 0) {
        root.style.setProperty('--page-nav-h', pageNav.offsetHeight + 'px');
      }
    };
    var ro = new ResizeObserver(update);
    ro.observe(nav);
    if (pageNav) ro.observe(pageNav);
    /* belt-and-braces: some environments miss element-resize on viewport
       changes; a plain resize listener costs nothing and closes that hole */
    window.addEventListener('resize', update, { passive: true });
    update();
  })();

  /* ───── Hero scroll cue → smooth-scroll to the Introduzione section ───── */
  (function () {
    var cue = document.querySelector('.hero-scroll-cue');
    var intro = document.querySelector('#intro');
    if (!cue || !intro) return;
    cue.addEventListener('click', function () {
      intro.scrollIntoView({ behavior: prefersReduced ? 'auto' : 'smooth', block: 'start' });
    });
  })();

  /* ───── Wordmark entrance (fade in at 200ms) ───── */
  (function () {
    if (prefersReduced) return;
    var wordmark = document.querySelector('.nav-wordmark img');
    if (!wordmark) return;
    wordmark.style.opacity = '0';
    wordmark.style.transition = 'opacity 800ms cubic-bezier(0.25, 1, 0.5, 1)';
    requestAnimationFrame(function () {
      setTimeout(function () { wordmark.style.opacity = '1'; }, 200);
    });
  })();

  /* ───── Introduzione manifesto — letter cascade on scroll-into-view ───── */
  (function () {
    var intro = document.querySelector('.intro');
    if (!intro) return;
    var manifesto = intro.querySelector('.intro-manifesto');
    if (!manifesto) return;

    function wrapChars(node) {
      if (node.nodeType === Node.TEXT_NODE) {
        var frag = document.createDocumentFragment();
        var text = node.nodeValue;
        for (var i = 0; i < text.length; i++) {
          var ch = text[i];
          if (ch === ' ' || ch === '\n' || ch === '\t') {
            frag.appendChild(document.createTextNode(ch));
          } else {
            var span = document.createElement('span');
            span.className = 'manifesto-char';
            span.textContent = ch;
            frag.appendChild(span);
          }
        }
        node.parentNode.replaceChild(frag, node);
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        Array.prototype.slice.call(node.childNodes).forEach(wrapChars);
      }
    }
    wrapChars(manifesto);

    var chars = manifesto.querySelectorAll('.manifesto-char');
    if (prefersReduced || !('IntersectionObserver' in window)) {
      chars.forEach(function (c) { c.classList.add('visible'); });
      return;
    }

    var LETTER_DELAY = 20;   // ms between letters
    var REVEAL_SETTLE = 700; // ms — let the .reveal fade-up settle first
    var started = false;

    function startCascade() {
      if (started) return;
      started = true;
      chars.forEach(function (c, i) {
        setTimeout(function () { c.classList.add('visible'); }, i * LETTER_DELAY);
      });
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          setTimeout(startCascade, REVEAL_SETTLE);
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.4 });
    observer.observe(intro);
  })();

  /* ───── Inquiry component — collapse behind the trigger; reopen on error ───── */
  (function () {
    var inquiries = document.querySelectorAll('.inquiry');
    if (!inquiries.length) return;
    inquiries.forEach(function (inq) {
      var trigger = inq.querySelector('.inquiry-trigger');
      var form = inq.querySelector('.inquiry-form');
      var cancel = inq.querySelector('.inquiry-cancel');
      if (!trigger || !form) return;
      // Posted successfully → confirmation is rendered server-side; leave it visible.
      // Shopify redirects back to the top of the page, so bring it into view — on mobile the
      // confirmation sits below the fold and the page otherwise reads as "nothing happened".
      if (inq.getAttribute('data-posted') === 'true') {
        var confirmation = inq.querySelector('.inquiry-confirmation');
        if (confirmation && 'scrollIntoView' in confirmation) {
          var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          requestAnimationFrame(function () {
            confirmation.scrollIntoView({
              behavior: reduce ? 'auto' : 'smooth',
              block: 'center'
            });
          });
        }
        return;
      }
      // Validation error → keep the form open so the user sees the message + their input.
      if (inq.getAttribute('data-open') !== 'true') {
        form.hidden = true;
        trigger.hidden = false;
      }
      trigger.addEventListener('click', function () {
        trigger.hidden = true;
        form.hidden = false;
        var first = form.querySelector('input, textarea');
        if (first) first.focus();
      });
      if (cancel) {
        cancel.addEventListener('click', function () {
          form.hidden = true;
          trigger.hidden = false;
          trigger.focus();
        });
      }
    });
  })();

  /* ───── Galleria in-page nav — active-section tracking ───── */
  (function () {
    var pageNav = document.querySelector('.page-nav');
    if (!pageNav || !('IntersectionObserver' in window)) return;
    var links = pageNav.querySelectorAll('a');
    var sections = document.querySelectorAll('.section[id]');
    if (!sections.length) return;
    var map = {};
    links.forEach(function (link) {
      var href = link.getAttribute('href') || '';
      if (href.charAt(0) === '#') map[href.slice(1)] = link;
    });
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          links.forEach(function (l) { l.classList.remove('is-current'); });
          var link = map[entry.target.id];
          if (link) link.classList.add('is-current');
        }
      });
    }, { rootMargin: '-30% 0px -50% 0px', threshold: 0 });
    sections.forEach(function (sec) { observer.observe(sec); });
  })();

  /* ───── Collection filters — client-side (Serie / Stato) ───── */
  (function () {
    var filters = document.querySelector('[data-filters]');
    var grid = document.querySelector('[data-grid]');
    if (!filters || !grid) return;
    var active = { series: 'all', status: 'all' };
    var cards = Array.prototype.slice.call(grid.querySelectorAll('.card'));
    var noResults = document.querySelector('[data-no-results]');
    var countEl = document.querySelector('[data-count]');

    function apply() {
      var visible = 0;
      cards.forEach(function (card) {
        var okSeries = active.series === 'all' || card.getAttribute('data-series') === active.series;
        var okStatus = active.status === 'all' || card.getAttribute('data-status') === active.status;
        var show = okSeries && okStatus;
        card.style.display = show ? '' : 'none';
        if (show) { visible++; }
      });
      if (noResults) { noResults.style.display = visible === 0 ? '' : 'none'; }
      if (countEl) {
        countEl.style.display = visible === 0 ? 'none' : '';
        // Rewrite the count to match the filtered view (it renders server-side
        // with the collection total). Pick the right plural form, then swap the
        // number in it — locale-safe for both IT and EN.
        var tpl = visible === 1
          ? countEl.getAttribute('data-count-one')
          : countEl.getAttribute('data-count-other');
        if (tpl) { countEl.textContent = tpl.replace(/\d[\d.,]*/, String(visible)); }
      }
    }
    filters.querySelectorAll('[data-filter-group]').forEach(function (group) {
      var key = group.getAttribute('data-filter-group');
      group.querySelectorAll('.filter-chip').forEach(function (chip) {
        chip.addEventListener('click', function () {
          group.querySelectorAll('.filter-chip').forEach(function (c) {
            c.classList.remove('is-active');
            c.setAttribute('aria-pressed', 'false');
          });
          chip.classList.add('is-active');
          chip.setAttribute('aria-pressed', 'true');
          active[key] = chip.getAttribute('data-filter-value');
          apply();
        });
      });
    });
  })();

  /* ───── Mobile gallery carousel pagination ─────
     Below 900px the gallery is a CSS scroll-snap carousel; these dots mirror the
     scroll position and let you tap to a slide. Desktop keeps the thumb strip. */
  (function () {
    var main = document.querySelector('.product-gallery-main');
    var dots = document.querySelector('[data-gallery-dots]');
    if (!main || !dots) return;
    var buttons = Array.prototype.slice.call(dots.querySelectorAll('.product-gallery-dot'));
    if (!buttons.length) return;

    var sync = function () {
      if (!main.clientWidth) return;
      var i = Math.round(main.scrollLeft / main.clientWidth);
      buttons.forEach(function (b, n) { b.classList.toggle('is-active', n === i); });
    };
    main.addEventListener('scroll', sync, { passive: true });
    buttons.forEach(function (b, n) {
      b.addEventListener('click', function () {
        main.scrollTo({ left: n * main.clientWidth, behavior: 'smooth' });
      });
    });
  })();

  /* ───── Mobile sticky purchase bar ─────
     Appears once the real purchase block leaves the viewport, so the CTA is
     always one tap away. Toggled with the [hidden] attribute (display:none) —
     NOT opacity: iOS 26 samples hidden fixed elements at the viewport edges
     when tinting browser chrome, so an "invisible" bar would still colour it. */
  (function () {
    var bar = document.querySelector('[data-sticky-bar]');
    var purchase = document.querySelector('.product-purchase');
    if (!bar || !purchase || !('IntersectionObserver' in window)) return;

    new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        // Only ever show it on the mobile layout.
        var mobile = window.matchMedia('(max-width: 900px)').matches;
        bar.hidden = entry.isIntersecting || !mobile;
      });
    }, { threshold: 0 }).observe(purchase);

    var addBtn = bar.querySelector('[data-sticky-add]');
    if (addBtn) {
      addBtn.addEventListener('click', function () {
        var realCta = document.querySelector('.product-cta');
        if (realCta) { realCta.click(); }
      });
    }
    var inquiryBtn = bar.querySelector('[data-sticky-inquiry]');
    if (inquiryBtn) {
      inquiryBtn.addEventListener('click', function () {
        purchase.scrollIntoView({ behavior: 'smooth', block: 'center' });
        var trigger = purchase.querySelector('.inquiry-trigger');
        if (trigger) { setTimeout(function () { trigger.click(); }, 400); }
      });
    }
    // Keep the sticky price in sync with the variant picker (ciotole sizes).
    var priceEl = bar.querySelector('[data-sticky-price]');
    var mainPrice = document.querySelector('[data-product-price]');
    if (priceEl && mainPrice && 'MutationObserver' in window) {
      new MutationObserver(function () {
        priceEl.textContent = mainPrice.textContent.trim();
      }).observe(mainPrice, { childList: true, characterData: true, subtree: true });
    }
  })();

  /* ───── Product lightbox (gallery) ───── */
  (function () {
    var lightbox = document.getElementById('lightbox');
    if (!lightbox) return;
    var zoomables = Array.prototype.slice.call(document.querySelectorAll('.product .zoomable'));
    if (!zoomables.length) { lightbox.parentNode && lightbox.setAttribute('hidden', ''); return; }

    var img = lightbox.querySelector('.lightbox-content img');
    var thumbs = lightbox.querySelector('.lightbox-thumbs');
    var images = zoomables.map(function (z) {
      var i = z.querySelector('img');
      return { src: i.currentSrc || i.src, alt: i.alt || '' };
    });
    var index = 0;
    var lastFocused = null;

    // Localized "View {index} of {total}" — supplied by product-detail.liquid so the
    // client-built thumbs don't speak Italian on the EN storefront.
    var labelTpl = lightbox.getAttribute('data-view-label') || 'View #I# of #T#';
    function viewLabel(i, total) {
      return labelTpl.replace('#I#', i).replace('#T#', total);
    }

    // Focus trap — same pattern as the mobile drawer. Queried live on each Tab
    // because the thumb buttons are rendered client-side per open().
    function focusables() {
      return Array.prototype.slice.call(lightbox.querySelectorAll(
        'a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])'
      )).filter(function (el) { return el.offsetParent !== null; });
    }

    function update() {
      img.src = images[index].src;
      img.alt = images[index].alt;
      thumbs.querySelectorAll('.lightbox-thumb').forEach(function (t, i) {
        t.classList.toggle('is-active', i === index);
      });
    }
    function renderThumbs() {
      thumbs.innerHTML = '';
      if (images.length < 2) return;
      images.forEach(function (im, i) {
        var b = document.createElement('button');
        b.type = 'button'; b.className = 'lightbox-thumb';
        b.setAttribute('aria-label', viewLabel(i + 1, images.length));
        b.addEventListener('click', function () { index = i; update(); });
        var t = document.createElement('img'); t.src = im.src; t.alt = '';
        b.appendChild(t); thumbs.appendChild(b);
      });
    }
    function openAt(i) {
      lastFocused = document.activeElement;
      index = i; renderThumbs(); update();
      lightbox.hidden = false;
      var sw = window.innerWidth - document.documentElement.clientWidth;
      if (sw > 0) document.body.style.paddingRight = sw + 'px';
      document.body.style.overflow = 'hidden';
      lightbox.querySelector('.lightbox-close').focus();
    }
    function close() {
      lightbox.hidden = true;
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      // Return focus to the image that opened it — without this, closing drops
      // focus to <body> and a keyboard user restarts at the top of the document.
      if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
      lastFocused = null;
    }
    function next() { index = (index + 1) % images.length; update(); }
    function prev() { index = (index - 1 + images.length) % images.length; update(); }

    // Make each zoomable openable by click AND keyboard (a11y audit item).
    zoomables.forEach(function (z, i) {
      z.setAttribute('tabindex', '0');
      z.setAttribute('role', 'button');
      z.addEventListener('click', function () { openAt(i); });
      z.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openAt(i); }
      });
    });
    lightbox.querySelector('.lightbox-close').addEventListener('click', close);
    lightbox.querySelector('.lightbox-prev').addEventListener('click', function (e) { e.stopPropagation(); prev(); });
    lightbox.querySelector('.lightbox-next').addEventListener('click', function (e) { e.stopPropagation(); next(); });
    lightbox.addEventListener('click', function (e) {
      if (e.target === lightbox || e.target.classList.contains('lightbox-main') || e.target.classList.contains('lightbox-content')) close();
    });
    document.addEventListener('keydown', function (e) {
      if (lightbox.hidden) return;
      if (e.key === 'Escape') { close(); return; }
      if (e.key === 'ArrowLeft') { prev(); return; }
      if (e.key === 'ArrowRight') { next(); return; }
      // Trap Tab inside the dialog (it is aria-modal, so focus must not escape).
      if (e.key === 'Tab') {
        var f = focusables();
        if (!f.length) return;
        var first = f[0];
        var last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault(); last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault(); first.focus();
        } else if (!lightbox.contains(document.activeElement)) {
          e.preventDefault(); first.focus();
        }
      }
    });

    // Footer CTA — mirrors the product's purchase action (locked design).
    var cta = lightbox.querySelector('.lightbox-cta');
    if (cta) {
      cta.addEventListener('click', function (e) {
        var act = cta.getAttribute('data-lb-action');
        if (act === 'cart') {
          e.preventDefault(); close();
          var addBtn = document.querySelector('.product-purchase .product-cta');
          if (addBtn) addBtn.click();
        } else if (act === 'inquiry') {
          e.preventDefault(); close();
          var target = document.querySelector('.product-purchase');
          if (target) target.scrollIntoView({ behavior: 'smooth', block: 'center' });
          var trigger = document.querySelector('.product-purchase .inquiry-trigger');
          if (trigger) setTimeout(function () { trigger.click(); }, 350);
        }
        // 'sold' is a plain <a> → let it navigate to the collection.
      });
    }
  })();

  /* ───── Product gallery — thumbnail rail swaps the main image in place ───── */
  (function () {
    var gallery = document.querySelector('[data-gallery]');
    if (!gallery) return;
    var slides = Array.prototype.slice.call(gallery.querySelectorAll('[data-gallery-slide]'));
    var thumbs = Array.prototype.slice.call(gallery.querySelectorAll('[data-gallery-thumb]'));
    if (slides.length < 2) return;
    function show(i) {
      i = (i + slides.length) % slides.length;
      slides.forEach(function (s, n) { s.classList.toggle('is-active', n === i); });
      thumbs.forEach(function (t, n) {
        var on = n === i;
        t.classList.toggle('is-active', on);
        if (on) { t.setAttribute('aria-current', 'true'); } else { t.removeAttribute('aria-current'); }
      });
    }
    thumbs.forEach(function (t, n) { t.addEventListener('click', function () { show(n); }); });
  })();

  /* ───── Customer addresses (/account/addresses): expand/collapse the add + edit
     forms, country→province dependency, delete-with-confirm. Guarded by
     [data-customer-addresses]; folded from Dawn's customer.js to keep one theme.js.
     CountryProvinceSelector comes from option_selection.js (loaded by the section);
     delete posts a self-built form so it doesn't depend on Shopify.postLink. ───── */
  (function () {
    var root = document.querySelector('[data-customer-addresses]');
    if (!root) return;

    if (window.Shopify && Shopify.CountryProvinceSelector) {
      new Shopify.CountryProvinceSelector('AddressCountryNew', 'AddressProvinceNew', {
        hideElement: 'AddressProvinceContainerNew',
      });
      root.querySelectorAll('[data-address-country-select]').forEach(function (select) {
        var id = select.dataset.formId;
        new Shopify.CountryProvinceSelector('AddressCountry_' + id, 'AddressProvince_' + id, {
          hideElement: 'AddressProvinceContainer_' + id,
        });
      });
    }

    function toggle(el) {
      if (el) el.setAttribute('aria-expanded', (el.getAttribute('aria-expanded') === 'false').toString());
    }

    root.querySelectorAll('button[aria-expanded]').forEach(function (btn) {
      btn.addEventListener('click', function () { toggle(btn); });
    });

    root.querySelectorAll('button[type="reset"]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var holder = btn.closest('[data-address]');
        if (holder) toggle(holder.querySelector('[aria-expanded]'));
      });
    });

    root.querySelectorAll('button[data-confirm-message]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (!window.confirm(btn.getAttribute('data-confirm-message'))) return;
        var form = document.createElement('form');
        form.method = 'post';
        form.action = btn.dataset.target;
        var method = document.createElement('input');
        method.type = 'hidden';
        method.name = '_method';
        method.value = 'delete';
        form.appendChild(method);
        document.body.appendChild(form);
        form.submit();
      });
    });
  })();

  /* ───── Variant picker (multi-variant products, e.g. the ciotole): each <select>
     swaps the add-to-cart variant id, the displayed price, and the button state. ───── */
  (function () {
    var picker = document.querySelector('[data-variant-picker]');
    if (!picker) return;
    var jsonEl = picker.querySelector('[data-variants-json]');
    var form = picker.closest('form');
    if (!jsonEl || !form) return;
    var variants;
    try { variants = JSON.parse(jsonEl.textContent); } catch (e) { return; }
    var selects = Array.prototype.slice.call(picker.querySelectorAll('[data-variant-option-index]'));
    var idInput = form.querySelector('[data-variant-id]');
    var priceEl = document.querySelector('[data-product-price]');
    var addBtn = form.querySelector('.product-cta');
    function update() {
      var chosen = selects.map(function (s) { return s.value; });
      var match = null;
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i];
        if (!v.options || v.options.length !== chosen.length) continue;
        var ok = true;
        for (var j = 0; j < chosen.length; j++) { if (v.options[j] !== chosen[j]) { ok = false; break; } }
        if (ok) { match = v; break; }
      }
      if (!match) return;
      if (idInput) idInput.value = match.id;
      if (priceEl && match.price) priceEl.textContent = match.price;
      if (addBtn) addBtn.disabled = !match.available;
    }
    selects.forEach(function (s) { s.addEventListener('change', update); });
    update();
  })();

  /* ───── Cart quantity — submit on change ─────
     Typing a quantity then hunting for "Aggiorna" is clunky on the one line that
     ever has multiples (the ciotole). `change` fires on blur/Enter, not per
     keystroke, so this is one round-trip. Submitted THROUGH the update button so
     the request is byte-identical to clicking it. The button itself is hidden via
     `.js` in theme.css and remains the no-JS fallback. */
  (function () {
    var inputs = document.querySelectorAll('.cart-qty-input');
    if (!inputs.length) return;
    inputs.forEach(function (input) {
      input.addEventListener('change', function () {
        var form = input.closest('form');
        if (!form) return;
        var updateBtn = form.querySelector('[name="update"]');
        if (updateBtn && typeof form.requestSubmit === 'function') form.requestSubmit(updateBtn);
        else if (updateBtn) updateBtn.click();
        else form.submit();
      });
    });
  })();
})();
